package org.teddapp.project.ui.skills

import androidx.compose.runtime.Composable
import org.teddapp.project.ui.nav.NavIntent
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.stateIn
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.tedd.db.Focus_session
import com.tedd.db.Milestone
import com.tedd.db.Objective
import com.tedd.db.Parcours_step
import com.tedd.db.Project
import com.tedd.db.ProjectStatus
import com.tedd.db.Skill
import com.tedd.db.Task
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreFacts
import org.teddapp.project.core.CoreParcours
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.core.CoreSession
import org.teddapp.project.core.CoreSnapshot
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.parcours.ParcoursFile
import org.teddapp.project.parcours.ParcoursImportResult
import org.teddapp.project.parcours.ParcoursParse
import org.teddapp.project.parcours.ParcoursPlan
import org.teddapp.project.ui.TeddTier

/**
 * Une compétence et ce qu'elle a coûté en temps. Les heures viennent des courses de focus :
 * celles rattachées directement, et celles passées sur les tâches d'un objectif rattaché.
 */
internal data class SkillStats(
    val skill: Skill,
    val minutes: Int,
    val sessions: List<Focus_session>,
    /** Vrai quand les heures viennent du noyau plutôt que d'une somme locale. */
    val fromCore: Boolean = false,
    /** Ce que le noyau annonce sur les paliers ; null tant qu'il n'a rien dit. */
    val corePalier: String? = null,
    val corePalierSuivant: String? = null,
    val coreHeuresAvantSuivant: Long? = null,
    val objectives: List<Objective>,
    val projects: List<Project> = emptyList(),
) {
    val hours: Double get() = minutes / 60.0

    /**
     * Palier courant, tel que le noyau l'annonce. Null sans noyau : l'interface
     * dit alors qu'elle ne sait pas, au lieu de recalculer une règle qui ne lui
     * appartient pas et qui divergerait à la première modification.
     */
    val tier: TeddTier? get() = TeddTier.fromCore(corePalier)

    /** Palier suivant, ou null au sommet comme en l'absence de noyau. */
    val nextTier: TeddTier? get() = TeddTier.fromCore(corePalierSuivant)

    /** Heures restantes avant le palier suivant, d'après le noyau. */
    val hoursToNext: Double? get() = coreHeuresAvantSuivant?.toDouble()

    /** Dix jetons vers la cible de la compétence (1 000 h par défaut). */
    val tokensToTarget: Int
        get() = if (skill.target_hours <= 0) 0 else ((hours / skill.target_hours * 100).toInt()).coerceIn(0, 100)

    /** Les auto-explications écrites en fin de course, de la plus récente à la plus ancienne. */
    val explanations: List<Focus_session>
        get() = sessions.filter { !it.note.isNullOrBlank() }.sortedByDescending { it.started_at }
}

/** Une étape de parcours et son avancement, mesuré sur ses feuilles (micro ou actions). */
internal data class ParcoursStepStats(
    val step: Parcours_step,
    val objectives: List<Objective>,
    val doneMinutes: Int,
    val doneCount: Int,
    val totalCount: Int,
    /** La prochaine chose à faire dans cette étape, ou null si elle est finie. */
    val nextTask: Task?,
    /** Les feuilles de l'étape, dans l'ordre du parcours. */
    val leaves: List<Task> = emptyList(),
) {
    val plannedMinutes: Int get() = step.planned_min.toInt()
    val done: Boolean get() = totalCount > 0 && doneCount == totalCount
    val percent: Int get() = if (plannedMinutes <= 0) 0 else (doneMinutes * 100 / plannedMinutes).coerceIn(0, 100)
    val isProject: Boolean get() = step.kind == "projet"
}

/** État du pilier Compétences : ce que je sais faire, et où j'en suis. */
internal class SkillsModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
    private val core: CoreSession? = null,
) {
    /** Les ressources des compétences, lues par l'assistant. */
    val resourceRepo get() = repos.skillResources

    /** Les dépôts, pour l'écran de l'assistant. */
    val repositories: TeddRepositories get() = repos

    /** La compétence dont l'assistant est ouvert, à la place de la fiche. */
    var assistantFor by mutableStateOf<String?>(null)

    /**
     * Les listes observées, chaudes [org.teddapp.project.ui.ScreenModels.HOT_MS] après
     * le départ de l'écran : y revenir ne relit rien, et une écriture ailleurs les tient à jour.
     */
    private fun <T> hot(flow: Flow<List<T>>) = flow.stateIn(scope, SharingStarted.WhileSubscribed(org.teddapp.project.ui.ScreenModels.HOT_MS), emptyList())
    internal val skillsFlow = hot(repos.skills.observeAll())
    internal val sessionsFlow = hot(repos.focusSessions.observeAll())
    internal val objectivesFlow = hot(repos.objectives.observeAll())
    internal val projectsFlow = hot(repos.projects.observeAll())
    internal val tasksFlow = hot(repos.tasks.observeAll())
    internal val milestonesFlow = hot(repos.milestones.observeAll())
    internal val stepsFlow = hot(repos.parcours.observeAll())
    internal val projectStatusesFlow = hot(repos.projectStatus.observeAll())
    internal val mindMapsFlow = hot(repos.mindMaps.observeAll())

    var skills by mutableStateOf<List<Skill>>(emptyList())
    var sessions by mutableStateOf<List<Focus_session>>(emptyList())
    var objectives by mutableStateOf<List<Objective>>(emptyList())
    var projects by mutableStateOf<List<Project>>(emptyList())
    var tasks by mutableStateOf<List<Task>>(emptyList())
    var milestones by mutableStateOf<List<Milestone>>(emptyList())
    var steps by mutableStateOf<List<Parcours_step>>(emptyList())
    var projectStatuses by mutableStateOf<List<ProjectStatus>>(emptyList())
    var mindMaps by mutableStateOf<List<com.tedd.db.Mind_map>>(emptyList())

    /** Compétence dont on propose la conversion en carte heuristique, et ce que ça créerait. */
    var convertingSkill by mutableStateOf<Skill?>(null)
    var convertPreview by mutableStateOf<Map<String, Int>>(emptyMap())

    fun mindMapsOf(skillId: String): List<com.tedd.db.Mind_map> = mindMaps.filter { it.skill_id == skillId }

    /** Ouvre la carte de la compétence si elle existe, sinon propose de la convertir. */
    fun openOrConvertMindMap(skill: Skill, onOpen: (String) -> Unit) {
        mindMapsOf(skill.id).firstOrNull()?.let { onOpen(it.id); return }
        scope.launch {
            convertPreview = repos.mindMaps.previewFromSkill(skill.id)
            convertingSkill = skill
        }
    }

    /**
     * Reconvertir une compétence déjà convertie met sa carte à jour : les éléments
     * nouveaux s'ajoutent, rien n'est écrasé, aucune seconde carte n'est créée.
     */
    fun updateMindMap(skill: Skill, onOpen: (String, Int) -> Unit) {
        val map = mindMapsOf(skill.id).firstOrNull() ?: return
        scope.launch {
            val added = repos.mindMaps.refreshFromSkill(map.id)
            androidx.compose.runtime.withFrameNanos { onOpen(map.id, added) }
        }
    }

    fun convertMindMap(skill: Skill, onOpen: (String) -> Unit) {
        convertingSkill = null
        scope.launch {
            // Le dialogue doit être retiré avant de changer d'écran : les deux dans la même
            // image font planter Compose dans le navigateur.
            // Le rappel d'image passe avant la recomposition : il faut deux images pour que
            // le dialogue soit vraiment retiré, et deux autres avant de changer d'écran.
            repeat(2) { androidx.compose.runtime.withFrameNanos { } }
            val id = repos.mindMaps.fromSkill(skill.id) ?: return@launch
            // Le changement d'écran se fait dans le rappel d'image lui-même : dans le
            // navigateur, une coroutine reprise par un événement de pointeur qui change
            // l'écran fait planter le traitement de cet événement.
            androidx.compose.runtime.withFrameNanos { onOpen(id) }
        }
    }

    /** Instantané du noyau : il fait autorité sur les heures et les paliers. */
    var coreSnapshot by mutableStateOf<CoreSnapshot?>(null)

    var selectedId by mutableStateOf<String?>(null)
    /** Le mot cherché dans la compétence ouverte. */
    var detailSearch by mutableStateOf("")

    /** Filtre de recherche : nom et intention. À vingt-sept compétences, indispensable. */
    var search by mutableStateOf("")

    /** Les archivées sont repliées : on les met de côté, on ne les efface pas. */
    var showArchived by mutableStateOf(false)

    /** Étiquette retenue, ou null pour tout voir. */
    var tagFilter by mutableStateOf<String?>(null)

    /** Vue d'ensemble plutôt que liste : la comparaison plutôt que le détail. */
    var overview by mutableStateOf(false)
    var editing by mutableStateOf<Skill?>(null)
    var creating by mutableStateOf(false)

    /**
     * Statistiques par compétence, la plus travaillée en premier. Dérivées des listes :
     * recalculées à leur changement seulement, et gardées d'une visite à l'autre. La
     * vue les lit des dizaines de fois par recomposition, elle ne doit pas les recalculer.
     */
    val stats: List<SkillStats> by derivedStateOf { computeStats() }

    /** Index des listes observées, reconstruit avec elles. */
    val index: SkillsIndex by derivedStateOf {
        SkillsIndex.build(tasks, milestones, objectives, projects, steps, projectStatuses)
    }

    internal fun computeStats(): List<SkillStats> {
            val tasksById = index.tasksById
            val objById = index.objectivesById
            val projById = index.projectsById
            // Compétence d'une tâche de projet : celle du projet parent.
            fun projectSkillOf(taskId: String?): String? {
                val t = tasksById[taskId ?: return null] ?: return null
                if (t.parent_kind != "project") return null
                return t.parent_id?.let { projById[it] }?.skill_id
            }
            // Objectif d'une tâche : soit son parent direct, soit l'objectif du jalon parent.
            fun objectiveOf(taskId: String?): Objective? {
                val t = tasksById[taskId ?: return null] ?: return null
                return when (t.parent_kind) {
                    "milestone" -> t.parent_id?.let { msId -> milestoneObjective[msId]?.let { objById[it] } }
                    else -> t.parent_id?.let { objById[it] }
                }
            }
            val fromCore = coreSnapshot?.competences.orEmpty()
            // Chaque course est classée une seule fois, puis rangée par compétence.
            val live = sessions.filter { it.is_deleted == 0L }
            val bySkill = live.groupBy { s ->
                s.skill_id ?: objectiveOf(s.task_id)?.skill_id ?: projectSkillOf(s.task_id)
            }
            val objectivesBySkill = objectives.groupBy { it.skill_id }
            val projectsBySkill = projects.groupBy { it.skill_id }
            // Tâches qu'une course de focus a déjà mesurées : leur temps prévu ne compte plus.
            val measured = live.mapNotNull { it.task_id }.toHashSet()
            return skills.map { skill ->
                val all = bySkill[skill.id].orEmpty()
                // Le noyau connaît les secondes exactes ; sans lui, on somme les courses locales.
                val fromCoreSkill = fromCore[skill.id]
                val coreMinutes = fromCoreSkill?.let { (it.secondes / 60L).toInt() }
                // Barrer une tâche, c'est l'avoir faite, donc y avoir passé son temps prévu.
                // Le noyau crédite déjà les feuilles de parcours ; on ajoute ici les tâches
                // rattachées par objectif ou projet qu'il ne connaît pas, et toutes quand il est absent.
                val linked = objectivesBySkill[skill.id].orEmpty()
                    .flatMap { o -> index.milestonesByObjective[o.id].orEmpty().flatMap { m -> index.tasksByParent[m.id].orEmpty() } }
                    .plus(projectsBySkill[skill.id].orEmpty().flatMap { p -> index.tasksByParent[p.id].orEmpty().filter { it.parent_kind == "project" } })
                // Il ne crédite que les parcours qu'on lui a déclarés : sans déclaration, ses
                // feuilles valent zéro minute et c'est encore à nous de les compter.
                val coreCreditsLeaves = coreMinutes != null && coreSnapshot?.parcours?.containsKey(skill.id) == true
                val checkedMinutes = linked
                    .filter { isDone(it) && it.id !in measured && (!coreCreditsLeaves || it.id !in index.leafSkill) }
                    .sumOf { (it.estimated_minutes ?: 0L).toInt() }
                SkillStats(
                    skill = skill,
                    minutes = (coreMinutes ?: all.sumOf { it.focus_minutes.toInt() }) + checkedMinutes,
                    fromCore = coreMinutes != null,
                    corePalier = fromCoreSkill?.palier,
                    corePalierSuivant = fromCoreSkill?.palierSuivant,
                    coreHeuresAvantSuivant = fromCoreSkill?.heuresAvantSuivant,
                    sessions = all,
                    objectives = objectivesBySkill[skill.id].orEmpty(),
                    projects = projectsBySkill[skill.id].orEmpty(),
                )
            }.sortedByDescending { it.minutes }
    }

    /** Table jalon → objectif, dérivée des jalons. */
    val milestoneObjective: Map<String, String> by derivedStateOf { milestones.associate { it.id to it.objective_id } }

    /**
     * Ce qu'on travaille en ce moment : tout sauf les archivées, filtré par la
     * recherche. C'est la liste que l'écran montre par défaut.
     */
    val visible: List<SkillStats>
        get() = matching
            .filter { it.skill.archived == 0L }
            // L'épinglage passe avant tout : c'est un choix explicite, il ne se
            // discute pas avec un tri.
            .sortedByDescending { it.skill.pinned }

    /** Les mises de côté, retrouvables mais hors du chemin. */
    val archived: List<SkillStats> get() = matching.filter { it.skill.archived != 0L }

    private val matching: List<SkillStats>
        get() {
            val q = search.trim().lowercase()
            val tag = tagFilter
            return stats.filter { st ->
                val texte = q.isEmpty() ||
                    st.skill.name.lowercase().contains(q) ||
                    st.skill.intention.orEmpty().lowercase().contains(q) ||
                    st.skill.tags.orEmpty().lowercase().contains(q)
                val etiquette = tag == null || tag in tagsOf(st.skill)
                texte && etiquette
            }
        }

    /**
     * Toutes les étiquettes en usage, par fréquence puis par ordre alphabétique.
     *
     * On ne tient pas de liste d'étiquettes à part : elles existent tant qu'une
     * compétence les porte, et disparaissent quand la dernière les lâche.
     */
    val allTags: List<String>
        get() = stats
            .flatMap { tagsOf(it.skill) }
            .groupingBy { it }
            .eachCount()
            .entries
            .sortedWith(compareByDescending<Map.Entry<String, Int>> { it.value }.thenBy { it.key })
            .map { it.key }

    /** Combien de compétences portent cette étiquette. */
    fun tagCount(tag: String): Int = stats.count { tag in tagsOf(it.skill) }

    /** Les familles en usage, par ordre alphabétique : ce que l'éditeur propose avant la saisie libre. */
    val allFamilies: List<String>
        get() = stats.map { familyOf(it.skill) }.filter { it != SANS_FAMILLE }.distinct().sorted()

    /** Nombre de compétences en jeu, filtre mis à part : pour dire « 11 sur 35 ». */
    val inPlayCount: Int get() = stats.count { it.skill.archived == 0L }

    /** Nombre d'archivées, recherche comprise, pour l'annoncer sans les déplier. */
    val archivedCount: Int get() = stats.count { it.skill.archived != 0L }

    val selected: SkillStats?
        get() = (visible + archived).firstOrNull { it.skill.id == selectedId } ?: visible.firstOrNull()

    /** Étiquettes d'une compétence, nettoyées et sans doublon. */
    fun tagsOf(skill: Skill): List<String> = parseTags(skill.tags)

    /** La famille d'une compétence : sa première étiquette. Sans étiquette, elle va dans « autres ». */
    fun familyOf(skill: Skill): String = tagsOf(skill).firstOrNull() ?: SANS_FAMILLE

    /**
     * La liste rangée par famille, chaque famille dans l'ordre alphabétique et « autres »
     * en dernier. À l'intérieur d'une famille, l'ordre de la liste reçue est conservé :
     * épinglées d'abord, puis heures investies.
     */
    fun byFamily(list: List<SkillStats>): List<Pair<String, List<SkillStats>>> =
        list.groupBy { familyOf(it.skill) }
            .entries
            .sortedWith(compareBy<Map.Entry<String, List<SkillStats>>> { it.key == SANS_FAMILLE }.thenBy { it.key })
            .map { it.key to it.value }

    /** Épingle ou dépingle : la compétence remonte en tête de la liste courante. */
    fun togglePinned(skill: Skill) {
        scope.launch { repos.skills.setPinned(skill.id, skill.pinned == 0L) }
    }

    /** Met de côté ou remet en jeu. Rien n'est perdu : les heures restent comptées. */
    fun toggleArchived(skill: Skill) {
        scope.launch {
            repos.skills.update(
                id = skill.id, name = skill.name, color = skill.color, icon = skill.icon,
                intention = skill.intention, targetHours = skill.target_hours, tags = skill.tags,
                pinned = skill.pinned != 0L, archived = skill.archived == 0L,
                orderIndex = skill.order_index, deadline = skill.deadline,
            )
        }
    }

    /** Minutes de focus non rattachées à une compétence. */
    val unclassifiedMinutes: Int
        get() {
            val claimed = stats.flatMap { it.sessions }.map { it.id }.toHashSet()
            return sessions.filter { it.is_deleted == 0L && it.id !in claimed }.sumOf { it.focus_minutes.toInt() }
        }

    fun save(
        existing: Skill?,
        name: String,
        intention: String,
        color: String,
        icon: String,
        targetHours: Long,
        tags: String?,
        deadline: String?,
    ) {
        scope.launch {
            if (existing == null) {
                val id = repos.skills.create(
                    name = name, color = color, icon = icon, intention = intention,
                    targetHours = targetHours, tags = tags, orderIndex = skills.size.toLong(), deadline = deadline,
                )
                // La compétence, puis sa cible : le noyau en tire les paliers.
                core?.recordAll(
                    TeddIdentity.USER_ID,
                    listOf(
                        CoreFacts.competenceCreated(id, name, intention.ifBlank { "Compétence" }),
                        CoreFacts.competenceTargetSet(id, targetHours),
                    ),
                )
            } else {
                repos.skills.update(
                    id = existing.id, name = name, color = color, icon = icon, intention = intention,
                    targetHours = targetHours, tags = tags, pinned = existing.pinned != 0L,
                    archived = existing.archived != 0L, orderIndex = existing.order_index, deadline = deadline,
                )
                if (existing.target_hours != targetHours) {
                    core?.record(TeddIdentity.USER_ID, CoreFacts.competenceTargetSet(existing.id, targetHours))
                }
            }
        }
        creating = false
        editing = null
    }

    /** Compétence pour laquelle on écrit une tentative de langue, ou null. */
    var languageFor by mutableStateOf<Skill?>(null)

    /** Dernière tentative écrite, gardée pour l'accusé de réception à l'écran. */
    var lastAttempt by mutableStateOf<String?>(null)

    /**
     * Une tentative de langue : ce qu'on a produit, et ce qu'on voulait dire. Le noyau
     * en tire l'écart entre l'intention et la production, sans corriger la forme.
     */
    fun recordLanguageAttempt(language: String, attempt: String, intention: String) {
        if (attempt.isBlank()) return
        core?.record(TeddIdentity.USER_ID, CoreFacts.languageAttempt(language.trim(), attempt.trim(), intention.trim()))
        lastAttempt = attempt.trim()
        languageFor = null
    }

    // ── Parcours ──────────────────────────────────────────────────────

    /** Plan lu depuis un fichier, en attente de confirmation. Rien n'est écrit tant qu'il est là. */
    var pendingImport by mutableStateOf<ParcoursPlan?>(null)

    /** Erreurs de lecture du dernier fichier, ou vide. */
    var importErrors by mutableStateOf<List<String>>(emptyList())

    /** Vrai pendant l'écriture en base : un parcours fait plus de mille lignes. */
    var importing by mutableStateOf(false)

    /** Ce que le dernier import a créé, pour l'annoncer une fois. */
    var lastImport by mutableStateOf<ParcoursImportResult?>(null)

    /** Zone de collage ouverte : le repli quand aucun sélecteur de fichier n'est branché. */
    var pasteOpen by mutableStateOf(false)

    /** Étapes du parcours dépliées dans la fiche. */
    var openSteps by mutableStateOf<Set<String>>(emptySet())

    /** Lit un fichier : soit un plan à confirmer, soit des erreurs à corriger. */
    fun readParcours(text: String) {
        when (val parsed = ParcoursFile.parse(text)) {
            is ParcoursParse.Ok -> { pendingImport = parsed.plan; importErrors = emptyList() }
            is ParcoursParse.Failed -> { pendingImport = null; importErrors = parsed.errors }
        }
    }

    /** Écrit le plan en attente, rattaché à la compétence ouverte si elle est nommée pareil, sinon à la sienne. */
    fun confirmImport(intoSkillId: String?) {
        val plan = pendingImport ?: return
        importing = true
        scope.launch {
            try {
                val result = repos.parcoursImporter.import(plan, intoSkillId)
                // Dans l'ordre : la compétence si elle est née ici, sa cible, puis la structure
                // du parcours. Le noyau dira ensuite l'étape courante et le palier.
                core?.recordAll(
                    TeddIdentity.USER_ID,
                    buildList {
                        if (result.skillCreated) add(CoreFacts.competenceCreated(result.skillId, plan.competence, "Parcours"))
                        add(CoreFacts.competenceTargetSet(result.skillId, result.targetHours))
                        add(
                            CoreFacts.parcoursDeclared(
                                result.skillId, plan.competence,
                                result.declaration.map { s ->
                                    CoreFacts.ParcoursStepFact(s.code, s.title, s.isProject, s.level, s.leaves.map { CoreFacts.ParcoursLeafFact(it.id, it.title, it.minutes, it.kind) })
                                },
                            ),
                        )
                    },
                )
                lastImport = result
                selectedId = result.skillId
                overview = false
                pendingImport = null
            } finally {
                importing = false
            }
        }
    }

    fun cancelImport() { pendingImport = null; importErrors = emptyList() }

    /** Parcours déjà redéclarés par ce modèle : un échec ne se rejoue pas à chaque recomposition. */
    private val redeclared = HashSet<String>()

    /** Cibles déjà déclarées, par compétence et valeur : même garde-fou. */
    private val declaredTargets = HashSet<String>()

    /**
     * Redéclare au noyau les parcours qu'il ne connaît pas.
     *
     * Un parcours arrivé par la synchronisation, ou importé pendant que le noyau était
     * absent, existe en base sans que le noyau ait jamais reçu sa déclaration : il voit
     * alors les feuilles cochées, mais sans leur durée, et n'en crédite aucune.
     */
    fun repairCoreParcours() {
        val session = core ?: return
        val snapshot = coreSnapshot ?: return
        // La cible d'abord : une compétence venue d'un autre appareil, ou d'avant le
        // noyau, a sa cible en base sans qu'il l'ait jamais reçue. Les paliers en dépendent.
        val targets = skills.mapNotNull { skill ->
            val known = snapshot.competences[skill.id]
            val key = "${skill.id}:${skill.target_hours}"
            if (known?.heuresCible == skill.target_hours || key in declaredTargets) return@mapNotNull null
            declaredTargets += key
            CoreFacts.competenceTargetSet(skill.id, skill.target_hours)
        }
        if (targets.isNotEmpty()) session.recordAll(TeddIdentity.USER_ID, targets)
        index.parcoursBySkill.forEach { (skillId, steps) ->
            if (skillId in snapshot.parcours || skillId in redeclared) return@forEach
            val skill = skills.firstOrNull { it.id == skillId } ?: return@forEach
            val facts = missingParcoursFacts(skill, steps, knownName = snapshot.competences[skillId]?.nom, isDone = ::isDone)
            if (facts.isEmpty()) return@forEach
            redeclared += skillId
            session.recordAll(TeddIdentity.USER_ID, facts)
        }
    }

    fun toggleStep(id: String) { openSteps = if (id in openSteps) openSteps - id else openSteps + id }

    /** Les objectifs dépliés dans le parcours : leurs jalons et leurs tâches se voient. */
    var openObjectives by mutableStateOf<Set<String>>(emptySet())
    fun toggleObjective(id: String) { openObjectives = if (id in openObjectives) openObjectives - id else openObjectives + id }

    /** La ligne qu'un résultat de recherche vient d'ouvrir : elle s'éclaire et se met en vue. */
    var revealed by mutableStateOf<String?>(null)
        private set
    /** Change à chaque ouverture, même de la même ligne : l'écran la remet en vue. */
    var revealTick by mutableStateOf(0)
        private set

    /** Ouvre l'endroit d'un résultat : déplie l'étape et l'objectif, puis éclaire la ligne. */
    fun reveal(target: SkillTarget?) {
        if (target == null) { revealed = "top"; revealTick++; return }
        target.stepId?.let { if (it !in openSteps) openSteps = openSteps + it }
        target.objectiveId?.let { if (it !in openObjectives) openObjectives = openObjectives + it }
        revealed = target.key
        revealTick++
    }

    fun clearReveal(key: String) { if (revealed == key) revealed = null }

    /** Une tâche est faite si elle est cochée ou rangée dans une colonne terminale : la règle de l'écran Projets. */
    fun isDone(task: Task): Boolean = task.status == "done" || task.status_id in index.terminalStatusIds

    /**
     * Coche ou décoche une micro ou une action. La progression remonte jusqu'à l'objectif ;
     * une action de projet change aussi de colonne, pour que le tableau dise la même chose.
     * Le noyau l'apprend par le crochet des dépôts, d'où que vienne le geste.
     */
    fun toggleTask(task: Task) {
        val wasDone = isDone(task)
        scope.launch {
            if (task.parent_kind == "project") {
                val columns = projectStatuses.filter { it.project_id == task.parent_id }.sortedBy { it.order_index }
                val target = if (wasDone) columns.firstOrNull { it.is_terminal == 0L } else columns.firstOrNull { it.is_terminal == 1L }
                if (wasDone && task.status == "done") repos.progress.toggleTask(task)
                if (!wasDone) repos.progress.toggleTask(task)
                if (target != null && target.id != task.status_id) repos.tasks.moveToStatus(task.id, target.id, task.order_index)
            } else {
                repos.progress.toggleTask(task)
            }
        }
    }

    /** Avancement d'un projet rattaché : actions faites sur actions au total. */
    fun projectProgress(projectId: String): Pair<Int, Int> {
        val actions = tasksOf(projectId).filter { it.parent_kind == "project" }
        return actions.count { isDone(it) } to actions.size
    }

    /** Le parcours d'une compétence tel que le noyau le voit, ou null tant qu'il ne le connaît pas. */
    fun coreParcoursOf(skillId: String): CoreParcours? = coreSnapshot?.parcours?.get(skillId)

    /** Le parcours d'une compétence : ses étapes dans l'ordre, avec ce qu'il reste à faire. Lu dans l'index, jamais recalculé ici. */
    fun parcoursOf(skillId: String): List<ParcoursStepStats> = index.parcoursBySkill[skillId].orEmpty()

    /** Jalons d'un objectif, dans l'ordre. */
    fun milestonesOf(objectiveId: String): List<Milestone> = index.milestonesByObjective[objectiveId].orEmpty()

    /** Micro d'un jalon, ou actions d'un projet, dans l'ordre. */
    fun tasksOf(parentId: String): List<Task> = index.tasksByParent[parentId].orEmpty()

    fun delete(skill: Skill) {
        scope.launch { repos.skills.softDelete(skill.id) }
        if (selectedId == skill.id) selectedId = null
        editing = null
    }

    /** Rattache ou détache un objectif : son temps compte alors pour la compétence. */
    fun toggleObjective(objectiveId: String, skillId: String?) {
        scope.launch { repos.skills.tagObjective(objectiveId, skillId) }
    }
}

/**
 * Les faits qui apprennent au noyau un parcours qu'il n'a jamais reçu : la compétence si
 * elle n'a pas de nom chez lui, la déclaration, puis les feuilles déjà faites. Vide tant
 * qu'une étape n'a pas ses feuilles : les listes observées n'ont pas fini d'arriver.
 */
internal fun missingParcoursFacts(
    skill: Skill,
    steps: List<ParcoursStepStats>,
    knownName: String?,
    isDone: (Task) -> Boolean,
): List<String> {
    if (steps.isEmpty() || steps.any { it.leaves.isEmpty() }) return emptyList()
    return buildList {
        // Sans `CompetenceCreated`, le noyau nomme la compétence par son identifiant.
        if (knownName == null || knownName == skill.id) add(CoreFacts.competenceCreated(skill.id, skill.name, "Parcours"))
        add(
            CoreFacts.parcoursDeclared(
                skill.id, skill.name,
                steps.map { s ->
                    CoreFacts.ParcoursStepFact(
                        s.step.code, s.step.title, s.isProject, s.step.level_name,
                        s.leaves.map { CoreFacts.ParcoursLeafFact(it.id, it.title, (it.estimated_minutes ?: 0L).toInt(), it.kind) },
                    )
                },
            ),
        )
        // La déclaration d'abord : le parcours ignore une feuille cochée avant elle.
        steps.forEach { s -> s.leaves.filter(isDone).forEach { add(CoreFacts.parcoursLeafCompleted(skill.id, it.id)) } }
    }
}

/**
 * Tables de recherche construites une fois par changement des listes observées.
 * Les vues ne filtrent plus : elles lisent une entrée de table.
 */
internal class SkillsIndex(
    val tasksById: Map<String, Task>,
    val objectivesById: Map<String, Objective>,
    val projectsById: Map<String, Project>,
    /** Enfants d'un jalon ou d'un projet, par ordre. */
    val tasksByParent: Map<String, List<Task>>,
    /** Jalons d'un objectif, par ordre. */
    val milestonesByObjective: Map<String, List<Milestone>>,
    /** Étapes d'un parcours, calculées avec leur avancement. */
    val parcoursBySkill: Map<String, List<ParcoursStepStats>>,
    /** Compétence de chaque feuille de parcours (micro ou action), par identifiant de tâche. */
    val leafSkill: Map<String, String>,
    /** Colonnes terminales des projets : une tâche qui y est rangée est faite. */
    val terminalStatusIds: Set<String>,
) {
    companion object {
        val EMPTY = SkillsIndex(emptyMap(), emptyMap(), emptyMap(), emptyMap(), emptyMap(), emptyMap(), emptyMap(), emptySet())

        fun build(
            tasks: List<Task>,
            milestones: List<Milestone>,
            objectives: List<Objective>,
            projects: List<Project>,
            steps: List<Parcours_step>,
            statuses: List<ProjectStatus>,
        ): SkillsIndex {
            val terminal = statuses.filter { it.is_terminal == 1L }.map { it.id }.toHashSet()
            val tasksByParent = tasks.groupBy { it.parent_id ?: "" }.mapValues { (_, l) -> l.sortedBy { it.order_index } }
            val msByObj = milestones.groupBy { it.objective_id }.mapValues { (_, l) -> l.sortedBy { it.order_index } }
            val objByStep = objectives.filter { it.step_id != null }.groupBy { it.step_id!! }.mapValues { (_, l) -> l.sortedBy { it.created_at } }
            val parcours = steps.groupBy { it.skill_id }.mapValues { (_, own) ->
                own.sortedBy { it.position }.map { step -> stepStats(step, objByStep[step.id].orEmpty(), msByObj, tasksByParent, terminal) }
            }
            val leafSkill = HashMap<String, String>()
            for (step in steps) {
                if (step.kind == "projet") {
                    step.project_id?.let { tasksByParent[it] }.orEmpty().filter { it.parent_kind == "project" }.forEach { leafSkill[it.id] = step.skill_id }
                } else {
                    objByStep[step.id].orEmpty().forEach { o ->
                        msByObj[o.id].orEmpty().forEach { m ->
                            tasksByParent[m.id].orEmpty().filter { it.parent_kind == "milestone" }.forEach { leafSkill[it.id] = step.skill_id }
                        }
                    }
                }
            }
            return SkillsIndex(
                tasksById = tasks.associateBy { it.id },
                objectivesById = objectives.associateBy { it.id },
                projectsById = projects.associateBy { it.id },
                tasksByParent = tasksByParent,
                milestonesByObjective = msByObj,
                parcoursBySkill = parcours,
                leafSkill = leafSkill,
                terminalStatusIds = terminal,
            )
        }

        private fun stepStats(
            step: Parcours_step,
            objectives: List<Objective>,
            msByObj: Map<String, List<Milestone>>,
            tasksByParent: Map<String, List<Task>>,
            terminal: Set<String>,
        ): ParcoursStepStats {
            fun done(t: Task) = t.status == "done" || t.status_id in terminal
            val leaves: List<Task> = if (step.kind == "projet") {
                step.project_id?.let { tasksByParent[it] }.orEmpty().filter { it.parent_kind == "project" }
            } else {
                objectives.flatMap { o -> msByObj[o.id].orEmpty().flatMap { m -> tasksByParent[m.id].orEmpty().filter { it.parent_kind == "milestone" } } }
            }
            return ParcoursStepStats(
                step = step,
                objectives = objectives,
                doneMinutes = leaves.filter(::done).sumOf { (it.estimated_minutes ?: 0L).toInt() },
                doneCount = leaves.count(::done),
                totalCount = leaves.size,
                // La première non faite dans l'ordre du parcours : les feuilles sont déjà rangées par parent puis par ordre.
                nextTask = leaves.firstOrNull { !done(it) },
                leaves = leaves,
            )
        }
    }
}

/**
 * Découpe une liste d'étiquettes écrite à la main : virgules, espaces en trop,
 * casse et doublons. « Données , ML, données » donne « données, ml ».
 */
internal fun parseTags(raw: String?): List<String> =
    raw.orEmpty()
        .split(',')
        .map { it.trim().lowercase() }
        .filter { it.isNotEmpty() }
        .distinct()

/** Nom de la famille des compétences sans étiquette. */
internal const val SANS_FAMILLE = "autres"

/** Remet une liste d'étiquettes sous la forme stockée, ou null si elle est vide. */
internal fun joinTags(tags: List<String>): String? = tags.joinToString(", ").takeIf { it.isNotBlank() }

@Composable
internal fun rememberSkillsModel(repos: TeddRepositories): SkillsModel {
    val core = LocalCoreSession.current
    // Gardé d'une rubrique à l'autre, avec ses listes chaudes : revenir ne relit rien.
    val model = org.teddapp.project.ui.rememberScreenModel(repos, core, "skills") { s -> SkillsModel(repos, s, core) }
    LaunchedEffect(Unit) {
        NavIntent.consumeSkill()?.let { model.selectedId = it; model.overview = false }
    }
    model.skills = model.skillsFlow.collectAsState().value
    model.sessions = model.sessionsFlow.collectAsState().value
    model.objectives = model.objectivesFlow.collectAsState().value
    model.projects = model.projectsFlow.collectAsState().value
    model.coreSnapshot = core?.snapshot?.collectAsState()?.value
    model.tasks = model.tasksFlow.collectAsState().value
    model.milestones = model.milestonesFlow.collectAsState().value
    model.steps = model.stepsFlow.collectAsState().value
    model.projectStatuses = model.projectStatusesFlow.collectAsState().value
    model.mindMaps = model.mindMapsFlow.collectAsState().value
    LaunchedEffect(model.coreSnapshot, model.index, model.skills) { model.repairCoreParcours() }
    return model
}
